import { apiSlice } from '@services/apiSlice';
import { NewsAndStories as endpoints } from '@services/endpoints';
import {
  NewsAndStoriesList,
  NewsAndStoriesPage,
  NewsListResponse,
  NewsPageResponse,
  NewsQueryParams,
} from './NewsAndStories.types';
import { isNewsAndStoriesTopicsBlock } from './NewsAndStories.helpers';

const newsApi = apiSlice
  .enhanceEndpoints({
    addTagTypes: ['News'],
  })
  .injectEndpoints({
    endpoints: (build) => ({
      fetchNewsAndStoriesPageData: build.query<NewsAndStoriesPage, void>({
        query: () => ({
          url: endpoints.fetchNewsAndStoriesData,
          params: { lang: window.lang, pageId: window.pageId },
        }),
        transformResponse: (response: NewsPageResponse) => {
          return {
            ...response,
            Description: response.Decsription,
            I18n: response.I18n,
            CategoryList: Object.entries({
              All: response.I18n.AllType,
              ...response.TypeList,
            }).map(([key, value]) => {
              return {
                Name: value?.toString(),
                Id: key,
              };
            }),
            YearsList: response.YearList,
          };
        },
      }),
      fetchNewsAndStories: build.query<NewsAndStoriesList, NewsQueryParams>({
        query: ({ pageSize, ...filter }) => ({
          url: endpoints.fetchNews,
          params: {
            lang: window.lang,
            pageId: window.pageId,
            pageSize: filter.page === 1 ? 10 : pageSize,
            ...filter,
          },
        }),
        transformResponse: (response: NewsListResponse) => {
          return {
            Topics: response.Topics,
            HasNextPage: response.NewsListingItems.length < response.TotalResults,
            Items: response.NewsListingItems,
            ItemsCount: response.NewsListingItems.length,
            TotalResults: response.TotalResults,
            Industries: response.Industries || [],
            Countries: response.Countries || [],
            Year: response.Year,
          };
        },
        serializeQueryArgs: ({ endpointName }) => {
          return endpointName;
        },
        merge: (current, incoming, meta) => {
          const isRequestWithNoResults =
            current.HasNextPage === false && incoming.Items.length === 0;

          if (isRequestWithNoResults) {
            return {
              HasNextPage: false,
              Items: [],
              ItemsCount: 0,
              TotalResults: 0,
            };
          }

          if (meta.arg.page === 1) {
            return incoming;
          }

          const incomingItems = incoming.Items.filter((item) => !isNewsAndStoriesTopicsBlock(item));
          const itemsCount = current.ItemsCount + incomingItems.length;

          return {
            HasNextPage: itemsCount < incoming.TotalResults,
            Topics: incoming.Topics,
            Items: [...current.Items, ...incomingItems],
            ItemsCount: itemsCount > incoming.TotalResults ? incoming.TotalResults : itemsCount,
            TotalResults: incoming.TotalResults,
            Industries: incoming.Industries || [],
            Countries: incoming.Countries || [],
            Year: incoming.Year,
          };
        },
      }),
    }),
    overrideExisting: false,
  });

export const { useFetchNewsAndStoriesPageDataQuery, useLazyFetchNewsAndStoriesQuery } = newsApi;
